/**
 * For each word, in order, whether the source text has a space after it.
 *
 * The spaced text the model returns inserts spaces between every word; the
 * `·` markers in the transliteration row must only appear where the source
 * text itself had a real space, so each token needs to know whether its
 * boundary was one.
 */
export function computeTrailingSpaces(alignText: string, words: string[]): boolean[] {
   // Fast path: spacing is the only difference between the concatenated words
   // and the source text (the normal case — the model only added spaces).
   // Map each character back to its source position and read directly whether
   // a space follows each word's last character.
   const strippedChars: string[] = []
   const sourceIndex: number[] = []
   for (let i = 0; i < alignText.length; i++) {
      if (alignText[i] !== ' ') {
         strippedChars.push(alignText[i])
         sourceIndex.push(i)
      }
   }

   if (words.join('') === strippedChars.join('')) {
      const result: boolean[] = []
      let cursor = 0
      for (const word of words) {
         cursor += word.length
         const lastCharAt = sourceIndex[cursor - 1]
         result.push(cursor < strippedChars.length && alignText[lastCharAt + 1] === ' ')
      }
      return result
   }

   // Fallback: the model rewrote or merged text, so the words are matched one
   // by one. A word that isn't found is marked false and skipped — the cursor
   // only ever advances past text that actually matched, so one miss cannot
   // desynchronize the words after it.
   const result: boolean[] = []
   let cursor = 0
   for (const word of words) {
      const found = alignText.indexOf(word, cursor)
      if (found === -1) {
         result.push(false)
         continue
      }
      let end = found + word.length
      let hasSpace = false
      while (end < alignText.length && alignText[end] === ' ') {
         hasSpace = true
         end += 1
      }
      result.push(hasSpace)
      cursor = end
   }
   return result
}
